// Small, dependency-free time helpers shared across cards, action tiles, and the action detail
// view. Keep these pure so they can be used in render without side effects.

/**
 * Format a millisecond age as a compact "last seen" label (e.g. "12s ago", "8m ago").
 * Returns null for nullish/invalid/negative input so callers can render their own fallback.
 * @param {number|null|undefined} ms Age in milliseconds.
 * @returns {string|null}
 */
export function formatAge(ms) {
    if (ms == null || !isFinite(ms) || ms < 0) return null;
    const seconds = Math.floor(ms / 1000);
    if (seconds < 60) return `${seconds}s ago`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
}

/**
 * Age in ms between now and a UTC ISO 8601 timestamp (e.g. "2026-07-27T13:04:11Z").
 * Uses Date.parse, which reliably handles the trailing "Z" (UTC) — unlike parsing a
 * locale-formatted display string, which is timezone-ambiguous.
 * @param {string|null|undefined} isoUtc
 * @param {number} [nowMs=Date.now()]
 * @returns {number|null} ms since the timestamp, or null if unparseable.
 */
export function ageMsFromIso(isoUtc, nowMs = Date.now()) {
    if (!isoUtc) return null;
    const t = Date.parse(isoUtc);
    if (isNaN(t)) return null;
    return nowMs - t;
}
